import { Grid3X3, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRecordsViewMode } from "@/components/records/records-view/records-view-mode";

interface RecordsViewToggleProps {
  /** Optional sizing from the host. */
  className?: string;
}

/**
 * Grid/list toggle for photos ([[records-view-toggle.spec]]): at the trailing
 * end of the photos page's title row ([[page-title-row.spec]]), and in a photos
 * report's header ([[report-detail-page.spec]] US-013). Both drive the one
 * photos preference.
 *
 * The host decides when to show it, as on the plans page: the photos page
 * while the project has records, a report while it holds items. An empty state
 * offers nothing to present either way.
 */
export function RecordsViewToggle({ className }: RecordsViewToggleProps) {
  const [viewMode, setViewMode] = useRecordsViewMode();

  return (
    <div
      role="group"
      aria-label="Photos view mode"
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded-md border bg-background p-0.5",
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Grid view"
        aria-pressed={viewMode === "grid"}
        onClick={() => setViewMode("grid")}
        className={cn(viewMode === "grid" && "bg-accent text-accent-foreground")}
      >
        <Grid3X3 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="List view"
        aria-pressed={viewMode === "list"}
        onClick={() => setViewMode("list")}
        className={cn(viewMode === "list" && "bg-accent text-accent-foreground")}
      >
        <List className="h-4 w-4" />
      </Button>
    </div>
  );
}
